import React from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, MenuItem, Select, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useTranslation } from 'react-i18next';
import {
  fetchTenantModelSet,
  modelSetFailureMessage,
  putTenantModelSet,
  type TenantModelSet,
} from '../../lib/modelSetApi';

/**
 * The model set the whole deployment runs on.
 *
 * Every option, its name and its description come from `GET /api/tenant/model-set`; the web names no
 * set. Persist-then-render: choosing a set writes it and then RE-READS, and the select shows what the
 * re-read says is stored, never the value that was clicked. A failed write therefore leaves the select
 * on the stored value and says why. Only administrators can write, so the Settings dialog renders this
 * section for an administrator alone (`SettingsDialog`); the server's refusal is still handled below.
 */
export function ModelSetSettings({ rowSx, quiet }: { rowSx: SxProps<Theme>; quiet: string }) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [data, setData] = React.useState<TenantModelSet | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [writeError, setWriteError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  // Identifies the load that is current. A write captures it and drops every later step once it has moved
  // (the section unmounted, or the UI language changed and the data is being re-read in the new one).
  const generation = React.useRef(0);

  // Read (and re-read on retry or a UI-language change, since names are localized by the backend).
  React.useEffect(() => {
    const controller = new AbortController();
    generation.current += 1;
    setBusy(false);   // a write invalidated below will never clear it itself
    setLoadError(null);
    fetchTenantModelSet(controller.signal)
      .then((c) => { if (!controller.signal.aborted) setData(c); })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(modelSetFailureMessage(e, t));
      });
    return () => {
      controller.abort();
      generation.current += 1;
    };
    // `t` changes with the language, which `language` already covers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, language]);

  const choose = async (setId: string) => {
    const mine = generation.current;
    const stale = () => generation.current !== mine;
    setBusy(true);
    setWriteError(null);
    try {
      await putTenantModelSet(setId);
      if (stale()) return;
      const fresh = await fetchTenantModelSet();
      if (!stale()) setData(fresh);
    } catch (e) {
      if (stale()) return;
      setWriteError(modelSetFailureMessage(e, t));
      // The write may have landed before the failure surfaced; show the stored truth either way.
      try {
        const fresh = await fetchTenantModelSet();
        if (!stale()) setData(fresh);
      } catch { /* the error above already tells the user */ }
    } finally {
      if (!stale()) setBusy(false);
    }
  };

  const heading = (
    <Typography
      sx={{
        fontSize: theme.custom.typography.typeRoles.overline.size,
        fontWeight: theme.custom.typography.fontWeight.semibold,
        color: quiet,
        textTransform: 'uppercase' as const,
        letterSpacing: theme.custom.typography.typeRoles.overline.letterSpacing,
        mb: 1,
      }}
    >
      {t('common:modelSet.title')}
    </Typography>
  );

  if (!data) {
    return (
      <Box data-testid="model-set-section">
        {heading}
        <Box sx={rowSx}>
          {loadError ? (
            <>
              <Alert severity="error" sx={{ flex: 1 }} data-testid="model-set-load-error">{loadError}</Alert>
              <Button size="small" onClick={() => setAttempt((n) => n + 1)} sx={{ textTransform: 'none' }}>
                {t('common:actions.retry')}
              </Button>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} data-testid="model-set-loading">
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: quiet }}>{t('common:modelSet.loading')}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  const chosen = data.sets.find((x) => x.id === data.set_id);

  return (
    <Box data-testid="model-set-section">
      {heading}
      <Box sx={rowSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <TuneOutlinedIcon sx={{ fontSize: 20, color: quiet, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: theme.custom.typography.fontWeight.semibold,
                fontSize: theme.custom.typography.typeRoles.body.size,
                color: theme.custom.colors.text.primary,
                lineHeight: 1.4,
              }}
            >
              {t('common:modelSet.label')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: theme.custom.typography.typeRoles.caption.size, color: quiet, lineHeight: 1.3 }}
              data-testid="model-set-description"
            >
              {chosen?.description ?? ''}
            </Typography>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
          <Select
            value={chosen ? data.set_id : ''}
            displayEmpty
            renderValue={(v) => data.sets.find((x) => x.id === String(v))?.display_name ?? t('common:modelSet.choose')}
            disabled={busy || data.sets.length === 0}
            onChange={(e) => { void choose(String(e.target.value)); }}
            inputProps={{ 'aria-label': t('common:modelSet.label'), 'data-testid': 'model-set-select' }}
            sx={{
              fontSize: theme.custom.typography.typeRoles.bodySm.size,
              borderRadius: theme.custom.borders.radius.md,
            }}
          >
            {!chosen && (
              <MenuItem value="" disabled>{t('common:modelSet.choose')}</MenuItem>
            )}
            {data.sets.map((s) => (
              <MenuItem key={s.id} value={s.id} data-testid={`model-set-option-${s.id}`}>
                {s.display_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1, fontSize: theme.custom.typography.typeRoles.caption.size, color: quiet, lineHeight: 1.3 }}
        data-testid="model-set-applies"
      >
        {t('common:modelSet.appliesAll')}
      </Typography>
      {data.sets.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>{t('common:modelSet.noneAvailable')}</Alert>
      )}
      {writeError && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setWriteError(null)} data-testid="model-set-write-error">
          {writeError}
        </Alert>
      )}
    </Box>
  );
}
